/**
 * MONSTER TOWN — THE SOUND OF THE TIME OF DAY. TIME-OF-DAY CREW (src/time/**).
 *
 * "Ambient audio follows: morning birds, daytime bustle, evening crickets,
 * quiet night" (expansion brief §1).
 *
 * HOW IT USES THE EXISTING SYSTEM, AND WHERE IT DOES NOT.
 * The audio crew owns `src/audio/**` and nobody else writes a line in it. Two
 * halves of this bed therefore go two different ways:
 *
 *  - THE ONE-SHOTS ARE THEIR CUES. A bird is `bus.emit('audio:cue', …)` on the
 *    already-shipped `bird` cue at a low volume — the same public entry point
 *    every other crew uses, already proven audible by `npm run audio-check`.
 *  - THE BED IS OURS, ON THEIR CONTEXT. A cue is a one-shot with a retrigger
 *    lockout; "air" and "crickets" are continuous, and faking them by machine-
 *    gunning a one-shot is exactly the wall of noise `RETRIGGER_MS` exists to
 *    prevent. So we take `audio.ensure()`'s buses — their AudioContext, their
 *    master gain, their mute button, their iOS unlock, their visibility
 *    suspend — and hang three nodes off the MUSIC bus. Turning the volume down,
 *    muting, hiding the tab and ducking for a voice line all keep working
 *    because none of that is reimplemented here.
 *
 * IT IS QUIET ON PURPOSE. Every gain in this file is a fraction of a fraction:
 * the music bus is already at 0.34 of master. This is the sound of a room being
 * alive, not a soundtrack, and at night it is nearly nothing — which is the
 * point of night.
 */

import { bus } from '../core/events'
import { audio } from '../audio/engine'

interface Bed {
  ctx: AudioContext
  /** Wind / air: looped noise through a lowpass. */
  air: GainNode
  airFilter: BiquadFilterNode
  /** Crickets and owls are scheduled one-shots into this. */
  fx: GainNode
}

let bed: Bed | null = null
let noiseBuffer: AudioBuffer | null = null

/** Seconds of in-context time already scheduled ahead of `currentTime`. */
let nextBird = 0
let nextCricket = 0
let nextOwl = 0
let counts = { birds: 0, crickets: 0, owls: 0 }

let rngState = 0x9e3779b9
function rnd(): number {
  rngState = (Math.imul(rngState, 1664525) + 1013904223) >>> 0
  return rngState / 0x100000000
}

/** Deterministic ambience under the harness. */
export function seedAmbience(n: number): void {
  rngState = n >>> 0 || 1
}

// ---------------------------------------------------------------------------
// the mix, as a function of the hour
// ---------------------------------------------------------------------------

export interface AmbienceMix {
  /** 0..1 wind/air bed level. */
  air: number
  /** 0..1 how often a bird calls. */
  birds: number
  /** 0..1 crickets. */
  crickets: number
  /** 0..1 chance of an owl at all. */
  owls: number
}

const ramp = (x: number, a: number, b: number): number => {
  if (a === b) return x >= b ? 1 : 0
  const t = (x - a) / (b - a)
  return t < 0 ? 0 : t > 1 ? 1 : t
}
/** 0 outside [a,d], 1 across [b,c], eased on the way in and out. */
const window4 = (h: number, a: number, b: number, c: number, d: number): number =>
  Math.min(ramp(h, a, b), 1 - ramp(h, c, d))

/**
 * THE TABLE. Hours, not phases, because "birds start before sunrise" is a real
 * thing a five-year-old will notice and it does not line up with a band edge.
 */
export function mixAt(hour: number): AmbienceMix {
  const h = ((hour % 24) + 24) % 24
  /**
   * A window that runs THROUGH MIDNIGHT cannot be written as a < b < c < d, and
   * the first version of this table quietly evaluated to zero for every one of
   * them — so two in the morning sounded exactly like two in the afternoon.
   * Shifting the small hours past 24 makes the night one unbroken window, the
   * same way `core/time.ts` lets its last band wrap.
   */
  const late = h < 12 ? h + 24 : h
  const dawnBirds = window4(h, 4.4, 5.6, 9.5, 11.5)
  const dayBirds = window4(h, 8, 10, 16.5, 18.5) * 0.4
  const eveBirds = window4(h, 16.5, 17.2, 18.6, 19.4) * 0.5
  return {
    // The air never stops; it just breathes down at night so the crickets have
    // somewhere to sit.
    air: 0.62 + 0.38 * window4(h, 6, 9, 17, 19.5) - 0.24 * window4(late, 20.5, 22, 28, 29),
    birds: Math.max(dawnBirds, dayBirds, eveBirds),
    crickets: Math.max(window4(h, 18.2, 19.6, 22.5, 24.2), window4(h, -0.2, 0.4, 3.6, 4.9) * 0.55),
    owls: window4(late, 20.5, 21.5, 27.5, 28.6),
  }
}

// ---------------------------------------------------------------------------
// the bed
// ---------------------------------------------------------------------------

function makeNoise(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer !== null) return noiseBuffer
  const len = Math.floor(ctx.sampleRate * 2)
  const b = ctx.createBuffer(1, len, ctx.sampleRate)
  const d = b.getChannelData(0)
  let last = 0
  for (let i = 0; i < len; i++) {
    // Lightly integrated white noise: closer to wind than to a hiss, and it
    // loops without a seam because both ends settle toward zero.
    const w = Math.random() * 2 - 1
    last = last * 0.94 + w * 0.06
    const fade = Math.min(1, Math.min(i, len - i) / (ctx.sampleRate * 0.05))
    d[i] = last * 3.2 * fade
  }
  noiseBuffer = b
  return b
}

function ensureBed(): Bed | null {
  if (bed !== null) return bed
  const buses = audio.ensure()
  const ctx = audio.context
  if (buses === null || ctx === null || ctx.state !== 'running') return null

  const air = ctx.createGain()
  air.gain.value = 0
  const airFilter = ctx.createBiquadFilter()
  airFilter.type = 'lowpass'
  airFilter.frequency.value = 620
  airFilter.Q.value = 0.4
  const src = ctx.createBufferSource()
  src.buffer = makeNoise(ctx)
  src.loop = true
  src.connect(airFilter)
  airFilter.connect(air)
  air.connect(buses.music)
  try {
    src.start(ctx.currentTime + 0.02)
  } catch {
    return null
  }

  const fx = ctx.createGain()
  fx.gain.value = 1
  fx.connect(buses.music)

  bed = { ctx, air, airFilter, fx }
  const t = ctx.currentTime
  nextBird = t + 1
  nextCricket = t + 0.6
  nextOwl = t + 12
  return bed
}

// ---------------------------------------------------------------------------
// one-shots we synthesise ourselves
// ---------------------------------------------------------------------------

function cricket(b: Bed, at: number, level: number): void {
  const { ctx } = b
  const g = ctx.createGain()
  g.gain.value = 0
  const band = ctx.createBiquadFilter()
  band.type = 'bandpass'
  band.frequency.value = 4200 + rnd() * 900
  band.Q.value = 14
  const src = ctx.createBufferSource()
  src.buffer = makeNoise(ctx)
  src.loop = true
  src.connect(band)
  band.connect(g)
  g.connect(b.fx)
  // Three short chirps — the rhythm is what makes it a cricket and not a hiss.
  let t = at
  for (let i = 0; i < 3; i++) {
    g.gain.setValueAtTime(0, t)
    g.gain.linearRampToValueAtTime(0.05 * level, t + 0.006)
    g.gain.linearRampToValueAtTime(0, t + 0.026)
    t += 0.042
  }
  src.start(at)
  src.stop(t + 0.06)
  src.onended = () => {
    try {
      g.disconnect()
      band.disconnect()
    } catch {
      /* already gone */
    }
  }
  counts.crickets++
}

function owl(b: Bed, at: number, level: number): void {
  const { ctx } = b
  const g = ctx.createGain()
  g.gain.value = 0
  const osc = ctx.createOscillator()
  osc.type = 'sine'
  const f = 300 + rnd() * 60
  osc.frequency.setValueAtTime(f, at)
  osc.frequency.exponentialRampToValueAtTime(f * 0.86, at + 0.28)
  osc.connect(g)
  g.connect(b.fx)
  for (const [start, dur] of [
    [at, 0.3],
    [at + 0.44, 0.34],
  ] as const) {
    g.gain.setValueAtTime(0, start)
    g.gain.linearRampToValueAtTime(0.05 * level, start + 0.07)
    g.gain.linearRampToValueAtTime(0, start + dur)
  }
  osc.start(at)
  osc.stop(at + 0.86)
  osc.onended = () => {
    try {
      g.disconnect()
    } catch {
      /* already gone */
    }
  }
  counts.owls++
}

// ---------------------------------------------------------------------------
// the update
// ---------------------------------------------------------------------------

let currentMix: AmbienceMix = { air: 0, birds: 0, crickets: 0, owls: 0 }

/**
 * Called from the time system every fixed step. Does nothing at all until the
 * kid's first touch has unlocked the audio context (iOS rule, see
 * `src/audio/engine.ts`), and nothing expensive ever: at most one scheduled
 * voice per call, and a `setTargetAtTime` on one gain.
 */
export function updateAmbience(hour: number, quiet = 1): void {
  const mix = mixAt(hour)
  currentMix = mix
  const b = ensureBed()
  if (b === null) return
  const now = b.ctx.currentTime

  // Air. `quiet` is the sleep fade pulling the whole world down under the bed.
  const target = 0.055 * mix.air * quiet
  b.air.gain.setTargetAtTime(target, now, 0.9)
  b.airFilter.frequency.setTargetAtTime(420 + 420 * mix.air, now, 1.2)

  if (mix.birds > 0.02 && now >= nextBird) {
    // Their cue, their table, their volume rules. Quiet enough to be weather.
    bus.emit('audio:cue', { cue: 'bird', volume: 0.16 + 0.16 * mix.birds * quiet })
    counts.birds++
    const gap = 2.4 + (1 - mix.birds) * 7 + rnd() * 4.5
    nextBird = now + gap
  } else if (mix.birds <= 0.02) {
    nextBird = Math.max(nextBird, now + 1.5)
  }

  if (mix.crickets > 0.03 && now >= nextCricket) {
    cricket(b, now + 0.03, mix.crickets * quiet)
    nextCricket = now + 0.34 + rnd() * 0.5
  } else if (mix.crickets <= 0.03) {
    nextCricket = Math.max(nextCricket, now + 0.8)
  }

  if (mix.owls > 0.2 && now >= nextOwl) {
    owl(b, now + 0.05, mix.owls * quiet)
    nextOwl = now + 22 + rnd() * 40
  } else if (mix.owls <= 0.2) {
    nextOwl = Math.max(nextOwl, now + 10)
  }
}

/**
 * THE BAND CHANGED — bring the next voice forward.
 *
 * The schedulers hold a "next one at" time that can be tens of seconds out (an
 * owl is deliberately rare). Cross into evening, or come back from the bed fade
 * into a brand new morning, and without this the town is silent for half a
 * minute at exactly the moment it is supposed to have changed character. Wired
 * to `time:phaseChanged` in `index.ts`.
 */
export function nudgeAmbience(): void {
  const b = bed
  if (b === null) return
  const now = b.ctx.currentTime
  nextBird = Math.min(nextBird, now + 0.6 + rnd() * 1.2)
  nextCricket = Math.min(nextCricket, now + 0.2)
  nextOwl = Math.min(nextOwl, now + 2 + rnd() * 6)
}

/** Diagnostics for `window.__time` and `tools/time-check.mjs`. */
export function ambienceState(): {
  ready: boolean
  mix: AmbienceMix
  air: number
  played: { birds: number; crickets: number; owls: number }
} {
  return {
    ready: bed !== null,
    mix: { ...currentMix },
    air: bed === null ? 0 : bed.air.gain.value,
    played: { ...counts },
  }
}

/** Test hook: forget what has been played. */
export function resetAmbienceCounts(): void {
  counts = { birds: 0, crickets: 0, owls: 0 }
}
